import {
  useGetModelsQuery,
  useGetUserSettingQuery,
  WORD_ADDIN_AVAILABLE_MODELS_SETTING,
  WORD_ADDIN_DEFAULT_MODEL_SETTING,
} from "../api/settingsApi";
import React, { useEffect, useMemo } from "react";
import { ChatModel } from "../types/model";
import { CircleAlertIcon } from "../../../components/icons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Tooltip } from "../../../components/ui/tooltip";
import { cn } from "../../../lib/utils";
import { availableModels, defaultModel, isModelAvailable } from "../utils/modelChoice";

type ModelSelectProps = {
  selectedModel: ChatModel | null;
  onChange: (model: ChatModel) => void;
};

/**
 * The picker lives in the composer's bottom strip, and all three of its states occupy
 * the same 26px slot. The slot used to be drawn from DraftChat, which meant the two
 * states DraftChat could not see — loading and failure — broke out of it, and a failed
 * model list pushed a full-width Alert into a row sized for a chip.
 *
 * No border: the strip already sits inside the composer's own outline, and a chip
 * around the model name made a second box inside a box for no gain.
 */
const SLOT = "inline-flex h-[26px] max-w-[170px] items-center";

const ModelSelect: React.FC<ModelSelectProps> = ({ selectedModel, onChange }) => {
  const { data: models, isLoading, isError, refetch } = useGetModelsQuery();
  const { data: availableModelsSetting, isLoading: isAvailableModelsLoading } =
    useGetUserSettingQuery(WORD_ADDIN_AVAILABLE_MODELS_SETTING);
  const { data: defaultModelSetting, isLoading: isDefaultModelLoading } = useGetUserSettingQuery(
    WORD_ADDIN_DEFAULT_MODEL_SETTING
  );

  // The settings have settled once they are no longer loading — whether they
  // resolved or 404'd. Auto-selection waits for that so a configured default
  // isn't beaten to the punch by the models list arriving first.
  const areSettingsSettled = !isAvailableModelsLoading && !isDefaultModelLoading;

  // The models the organisation has allowed this add-in to use. The rules live in
  // modelChoice so that surfaces without a picker land on the same model.
  const available = useMemo(
    () => (models ? availableModels(models, availableModelsSetting) : undefined),
    [models, availableModelsSetting]
  );

  useEffect(() => {
    if (!available || available.length === 0 || !areSettingsSettled) {
      return;
    }

    // Re-select whenever the current choice is not among the available models —
    // this covers the initial seed, and a model disappearing mid-session because
    // it was dropped from Available Models or disabled for the region.
    if (isModelAvailable(available, selectedModel)) {
      return;
    }

    const model = defaultModel(available, defaultModelSetting);
    if (model) onChange(model);
  }, [available, selectedModel, areSettingsSettled, defaultModelSetting, onChange]);

  const handleChange = (selectedObjectIdentifier: string) => {
    const model = available?.find((m) => m.objectIdentifier === selectedObjectIdentifier);
    if (model) {
      onChange(model);
    }
  };

  if (available) {
    const selectedName = available.find(
      (m) => m.objectIdentifier === selectedModel?.objectIdentifier
    )?.name;
    return (
      <div className={SLOT}>
        <Select value={selectedModel?.objectIdentifier || ""} onValueChange={handleChange}>
          <SelectTrigger
            // No visible label -- the picker is a word in a strip, not a form field --
            // so it needs a spoken one, on the combobox itself.
            aria-label="Model"
            className="h-[26px] w-auto max-w-full gap-1 rounded-md border-0 pr-0.5 pl-0 text-[12px] text-muted-foreground hover:border-0 focus-visible:ring-[3px] focus-visible:ring-ring/40"
          >
            {/* The trigger shows the name alone, where each option also names its host
                beneath. Given children, SelectValue draws them instead of the
                option's text. */}
            <SelectValue>{selectedName ?? ""}</SelectValue>
          </SelectTrigger>
          <SelectContent align="start">
            {available.map((model) => (
              <SelectItem
                key={model.objectIdentifier}
                value={model.objectIdentifier}
                className="py-1"
              >
                <span className="flex flex-col">
                  <span className="text-body2 text-foreground">{model.name}</span>
                  <span className="text-caption text-muted-foreground">{model.hostedBy}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={SLOT}>
        <span className="truncate text-caption text-muted-foreground">Loading models…</span>
      </div>
    );
  }

  if (isError) {
    return (
      <Tooltip title="The model list could not be loaded. Select to try again.">
        <button
          type="button"
          onClick={() => void refetch()}
          className={cn(
            SLOT,
            "gap-1 text-error outline-none hover:underline focus-visible:underline"
          )}
        >
          <CircleAlertIcon className="size-3.5" />
          <span className="truncate text-caption">No models</span>
        </button>
      </Tooltip>
    );
  }

  return null;
};

export default ModelSelect;
